/**
 * ESTADO DEL TUTORIAL.
 *
 * Guarda si ya se hizo el recorrido, en qué paso va y si está abierto ahora.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ SE GUARDA EN EL DISPOSITIVO Y NO EN EL SERVIDOR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Porque "ya vi el tutorial" es una propiedad de la PERSONA en SU aparato, no
 * del negocio. El dueño que ya lo hizo en su computadora vuelve a necesitarlo
 * la primera vez que abre Softfi en el celular, donde la pantalla es otra y los
 * botones están en otro sitio.
 *
 * Además así no gastamos una petición al servidor en cada arranque para
 * preguntar algo que solo sirve para mostrar un aviso.
 */

import { create } from "zustand";

const CLAVE = "softfi.tutorial";

type Guardado = {
  terminado: boolean;
  /** Para poder ofrecer "continuar donde lo dejaste". */
  ultimoPaso: number;
};

function leer(): Guardado {
  try {
    const crudo = localStorage.getItem(CLAVE);
    if (!crudo) return { terminado: false, ultimoPaso: 0 };
    return { terminado: false, ultimoPaso: 0, ...(JSON.parse(crudo) as Partial<Guardado>) };
  } catch {
    return { terminado: false, ultimoPaso: 0 };
  }
}

function guardar(datos: Guardado): void {
  try {
    localStorage.setItem(CLAVE, JSON.stringify(datos));
  } catch {
    /* Modo incógnito: el tutorial funciona igual, solo no se recuerda. */
  }
}

type EstadoTutorial = {
  abierto: boolean;
  paso: number;
  terminado: boolean;
  /** Se dejó a medias y se puede retomar. */
  aMedias: boolean;

  abrir: (desdeElPrincipio?: boolean) => void;
  cerrar: () => void;
  siguiente: (total: number) => void;
  anterior: () => void;
  terminar: () => void;
  /** Para el botón "volver a ver el tutorial" de Ajustes. */
  reiniciar: () => void;
};

const inicial = typeof window === "undefined" ? { terminado: false, ultimoPaso: 0 } : leer();

export const useTutorial = create<EstadoTutorial>((set, get) => ({
  abierto: false,
  paso: inicial.ultimoPaso,
  terminado: inicial.terminado,
  aMedias: !inicial.terminado && inicial.ultimoPaso > 0,

  abrir: (desdeElPrincipio = false) => {
    const paso = desdeElPrincipio ? 0 : get().paso;
    set({ abierto: true, paso });
  },

  /**
   * Cerrar NO es lo mismo que terminar.
   *
   * Se guarda el paso para poder ofrecer "sigue donde lo dejaste", pero no se
   * marca como visto: quien cierra a la mitad no ha aprendido lo que falta.
   */
  cerrar: () => {
    const { paso } = get();
    guardar({ terminado: false, ultimoPaso: paso });
    set({ abierto: false, aMedias: paso > 0 });
  },

  siguiente: (total) => {
    const siguiente = get().paso + 1;
    if (siguiente >= total) {
      get().terminar();
      return;
    }
    guardar({ terminado: false, ultimoPaso: siguiente });
    set({ paso: siguiente });
  },

  anterior: () => set({ paso: Math.max(0, get().paso - 1) }),

  terminar: () => {
    guardar({ terminado: true, ultimoPaso: 0 });
    set({ abierto: false, terminado: true, aMedias: false, paso: 0 });
  },

  reiniciar: () => {
    guardar({ terminado: false, ultimoPaso: 0 });
    set({ abierto: true, paso: 0, terminado: false, aMedias: false });
  },
}));
